import { z } from 'zod';
import type { GameMeta, GameStatus, VersionNode } from './schemas/game.js';

/**
 * Version-action rules (feat/vtree-ui VT-001..VT-003; plan plans/fleet-vt-1.md).
 * The PURE decision logic behind the four version-mutation routes
 * (restore / branch / archive / unarchive) — every 404/409/no-op rule lives
 * here so it is unit-testable offline (D-054 pattern: handlers stay thin and
 * do I/O; decisions are pure functions). The handler module
 * (infra/lambda/api/games-versions.ts) maps an assessment to the HTTP
 * response, then enforces the same invariants atomically in its
 * TransactWriteCommand conditions (the race backstop — see plan §4 R-2).
 *
 * Invariants encoded:
 *  - soft-archive only, never delete; archived nodes are tombstones (D-039)
 *  - the CURRENT version is always live + playable (block archive-of-current;
 *    restore/branch targets must be un-archived AND have an artifact)
 *  - no mutating versions of an archived game
 *  - restore never downgrades a published game's status (plan §4 R-1)
 *
 * HOW TO DISABLE / REMOVE: the consuming routes gate on
 * FEATURES.versionActions (404 when off). Full removal = this file (+ test) +
 * games-versions.ts + the games.ts dispatch lines + the 4 api-routes entries.
 */

/** Body of POST /games/{gameId}/versions/{versionId}/branch (VT-002). */
export const BranchVersionInput = z.object({
  /** The user's instruction for the new lineage (same bounds as job prompts). */
  prompt: z.string().min(1).max(2000),
  /** Optional label for the branch, copied onto the produced VersionNode. */
  branchLabel: z.string().min(1).max(120).optional(),
});
export type BranchVersionInput = z.infer<typeof BranchVersionInput>;

export type VersionAction = 'restore' | 'branch' | 'archive' | 'unarchive';

export type VersionActionAssessment =
  /** Proceed. `noop: true` = already in the desired state → 200 without a write. */
  | { ok: true; noop: boolean }
  | { ok: false; status: 404 | 409; error: string };

const proceed: VersionActionAssessment = { ok: true, noop: false };
const noop: VersionActionAssessment = { ok: true, noop: true };
const block = (status: 404 | 409, error: string): VersionActionAssessment => ({
  ok: false,
  status,
  error,
});

/**
 * Assess a version action against the game meta + the target node (as read by
 * the handler; `node` undefined = no such `ver#` item). Pure — no I/O.
 */
export function assessVersionAction(
  action: VersionAction,
  meta: Pick<GameMeta, 'archived' | 'currentVersionId'>,
  node: Pick<VersionNode, 'versionId' | 'archived' | 'artifactKey'> | undefined,
): VersionActionAssessment {
  if (!node) return block(404, 'version not found');
  if (meta.archived) return block(409, 'game is archived');

  switch (action) {
    case 'restore':
      if (node.archived) return block(409, 'version is archived — unarchive it first');
      if (!node.artifactKey) return block(409, 'version has no playable artifact');
      if (node.versionId === meta.currentVersionId) return noop; // already current
      return proceed;

    case 'branch':
      // The runner seeds the new lineage from the parent artifact — an
      // artifact-less parent would silently degenerate to a from-scratch
      // generate (plan §4 R-3).
      if (node.archived) return block(409, 'cannot branch from an archived version');
      if (!node.artifactKey) return block(409, 'version has no playable artifact');
      return proceed;

    case 'archive':
      // Dummy-proof delete: never strand `currentVersionId` on an archived
      // node — owner Play and the public link both resolve current at request
      // time. Explicitly blocked (no surprise auto-re-point, VT-003).
      if (node.versionId === meta.currentVersionId)
        return block(409, 'cannot archive the current version — restore another version first');
      if (node.archived) return noop; // already archived
      return proceed;

    case 'unarchive':
      if (!node.archived) return noop; // already live
      return proceed;
  }
}

/**
 * Game status after `currentVersionId` is (re)pointed at a playable artifact
 * (VT-001 plan §4 R-1; generalized in TE-001): a published game STAYS
 * published (the public link serves whatever is current at request time —
 * re-pointing must not darken it; play-public.ts 404s any non-published
 * status); anything else becomes `playable` (current now holds, by definition,
 * a playable artifact). TWO call sites, same rule:
 *  - restore (games-versions.ts#restoreVersion)
 *  - a new generation landing (job-result.ts#onCompleted step 3)
 */
export function statusAfterCurrentVersionChange(status: GameStatus): GameStatus {
  return status === 'published' ? 'published' : 'playable';
}
